import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  createComponent,
  inject,
} from '@angular/core';

import { MAX_ALLOWED_PLAYBACK_RATE } from '@core/constants/video.constant';
import { VideoControlsComponent } from '@shared/video-controls/video-controls.component';

/** Marks a video we have already taken over, so repeated scans skip it. */
const MANAGED_ATTRIBUTE = 'data-playback-speed-managed';

/**
 * Enforces the company's `enableVideoPlaybackSpeed` setting on every `<video>` in the page.
 *
 * The browser's own control bar lives in a closed shadow DOM: its playback-speed menu cannot be
 * read, styled or filtered, and it sets `playbackRate` on the element directly. So while the
 * restriction is active we switch the native controls off and mount our own bar, which only ever
 * offers rates up to 1x. A capture-phase `ratechange` listener stays as a safety net for videos
 * we have not taken over yet — `ratechange` does not bubble, but capture still reaches them.
 *
 * Audio is deliberately out of scope (product decision on sc-5745).
 */
@Injectable({
  providedIn: 'root',
})
export class VideoPlaybackSpeedService {
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);

  private restricting = false;
  private observer?: MutationObserver;
  private readonly mounted = new Map<
    HTMLVideoElement,
    ComponentRef<VideoControlsComponent>
  >();

  /**
   * Turns the restriction on or off. Safe to call repeatedly with the same value — takeover and
   * teardown each happen once.
   */
  setRestricted(restricted: boolean): void {
    if (restricted === this.restricting) return;
    this.restricting = restricted;

    if (restricted) {
      document.addEventListener('ratechange', this.clampRate, true);
      this.takeOverAll();
      this.observer = new MutationObserver(() => this.takeOverAll());
      this.observer.observe(document.body, { childList: true, subtree: true });
    } else {
      document.removeEventListener('ratechange', this.clampRate, true);
      this.observer?.disconnect();
      this.observer = undefined;
      this.releaseAll();
    }
  }

  /**
   * Swaps native controls for ours on every video that needs it.
   *
   * Players that own their DOM — H5P dialogs especially — re-render after we mount, which throws
   * our bar away while the video element itself survives. So a video already marked as managed is
   * re-mounted whenever its bar is no longer in the document.
   */
  private takeOverAll(): void {
    // Drop anything whose video has since left the page.
    [...this.mounted.entries()].forEach(([video, ref]) => {
      if (!video.isConnected) this.unmount(video, ref);
    });

    document.querySelectorAll<HTMLVideoElement>('video').forEach(video => {
      const existing = this.mounted.get(video);
      if (existing) {
        if (existing.location.nativeElement.isConnected) return;
        this.unmount(video, existing);
        this.takeOver(video);
        return;
      }
      // Only players that hand control to the browser need taking over. A video without
      // `controls` draws its own UI — H5P Interactive Video does — and that menu is already
      // filtered through the video handlers, so mounting a second bar would just duplicate it.
      if (video.hasAttribute('controls')) this.takeOver(video);
    });
  }

  /** Tears down one mounted bar without restoring the native controls. */
  private unmount(
    video: HTMLVideoElement,
    ref: ComponentRef<VideoControlsComponent>,
  ): void {
    this.appRef.detachView(ref.hostView);
    ref.destroy();
    ref.location.nativeElement.remove();
    this.mounted.delete(video);
  }

  private takeOver(video: HTMLVideoElement): void {
    video.setAttribute(MANAGED_ATTRIBUTE, '');
    video.removeAttribute('controls');
    if (video.playbackRate > MAX_ALLOWED_PLAYBACK_RATE) {
      video.playbackRate = MAX_ALLOWED_PLAYBACK_RATE;
    }

    // Use the component's own tag as the host so the bar is identifiable in the DOM.
    const host = document.createElement('app-video-controls');
    // Sit directly under the video so the bar tracks it through layout changes.
    video.insertAdjacentElement('afterend', host);

    const ref = createComponent(VideoControlsComponent, {
      environmentInjector: this.injector,
      hostElement: host,
    });
    ref.setInput('video', video);
    ref.setInput('maxRate', MAX_ALLOWED_PLAYBACK_RATE);
    this.appRef.attachView(ref.hostView);
    ref.changeDetectorRef.detectChanges();
    this.mounted.set(video, ref);
  }

  /** Restores every video to its native controls. */
  private releaseAll(): void {
    [...this.mounted.entries()].forEach(([video, ref]) => {
      this.unmount(video, ref);
      video.removeAttribute(MANAGED_ATTRIBUTE);
      video.setAttribute('controls', '');
    });
    this.mounted.clear();
  }

  /**
   * Safety net for any video not yet taken over: native menus cannot be altered, so we let the
   * viewer pick any rate and snap the effective rate back to 1x. Slower playback stays available.
   */
  private readonly clampRate = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof HTMLVideoElement)) return;
    if (target.playbackRate > MAX_ALLOWED_PLAYBACK_RATE) {
      target.playbackRate = MAX_ALLOWED_PLAYBACK_RATE;
    }
  };
}
